import React, { useEffect, useState } from 'react';

import { useFlags } from 'flagsmith/react';

import { Platform, SubscriptionTier } from '@invoice-simple/common';
import { FeatureName } from '@invoice-simple/feature-gate';
import { PaywallCouponConfig } from '@invoice-simple/is-coupon';

import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ISIntl } from 'src/i18n';
import alertModel from 'src/models/AlertModel';
import UserModel, { Cadence } from 'src/models/UserModel';
import { AppStore } from 'src/stores/AppStore';
import { titleizeAll } from 'src/util/Titleize';
import { getURLQueryParam, URLQueryParamKeys } from 'src/util/url';
import { LoadingSpinner } from '../Button';
import { PriceDisplay, SubscriptionBadge } from './components';
import { BackButton } from './components/BackButton';
import { BookkeepingUpsellModal } from './components/BookkeepingUpsellModal';
import { PlanIntervalSelector } from './components/PlanIntervalSelector';
import { ValueProps } from './components/ValueProps';
import { useInterval } from './hooks/useInterval';
import { useUpgrade } from './hooks/useUpgrade';
import { getDiscountTranslation, getIntervalText, getSavingsText, getTierTitle } from './utils';
import { getAlertMessageFromFeature } from './utils/alert.utils';
import { messages } from './utils/messages';

interface Props {
  store: AppStore;
  intl: ISIntl;
}

interface PaywallCardProps {
  intl: ISIntl;
  tier: SubscriptionTier;
  selectedInterval: Cadence;
  user: UserModel;
  features: string[];
  coupon?: PaywallCouponConfig;
  loading: boolean;
  shouldDisallowSwitch: (tier: SubscriptionTier, user: any) => boolean;
  onUpgrade: (tier: SubscriptionTier) => void;
  isCurrentPlan: (tier: SubscriptionTier) => boolean;
  OriginalPrice: React.FC<{ tier: SubscriptionTier; shouldHidePerYear?: boolean }>;
  FinalPrice: React.FC<{
    tier: SubscriptionTier;
    isTieredMatrix?: boolean;
    coupon?: PaywallCouponConfig;
  }>;
  getCouponPrices: (
    coupon: PaywallCouponConfig,
    tier: SubscriptionTier,
    cadence: Cadence
  ) => { originalPriceDisplay: number };
}

const PaywallCard: React.FC<PaywallCardProps> = ({
  intl,
  tier,
  selectedInterval,
  user,
  features,
  coupon,
  loading,
  shouldDisallowSwitch,
  onUpgrade,
  isCurrentPlan,
  OriginalPrice,
  FinalPrice,
  getCouponPrices
}) => {
  const { ft, fta } = intl;
  const intervalText = getIntervalText(selectedInterval, ft);

  const getButtonText = () => {
    return isCurrentPlan(tier) ? fta(messages.currentActivePlan) : ft(messages.upgrade);
  };

  const getOriginalPriceDisplay = (coupon: PaywallCouponConfig): number => {
    return getCouponPrices(coupon, tier, selectedInterval).originalPriceDisplay;
  };

  return (
    <div className="flex flex-col w-[300px] border rounded-xl shadow-md">
      <div className="border-bottom border-gray-200 p-[24px]">
        <span className="w-1/4 pb-4 text-left text-3xl font-medium text-gray-900">
          {titleizeAll(getTierTitle(tier))}
        </span>
        <p className={'mt-10 flex flex-col'}>
          {!coupon && selectedInterval === Cadence.ANNUAL && (
            <span className="font-medium">
              <OriginalPrice tier={tier} shouldHidePerYear={true} />
            </span>
          )}
          {coupon && (
            <PriceDisplay
              price={getOriginalPriceDisplay(coupon)}
              cadence={selectedInterval}
              strikeThrough
            />
          )}
          <span className="text-4xl font-extrabold tracking-tight text-gray-900 my-1">
            <FinalPrice tier={tier} isTieredMatrix={true} coupon={coupon} />{' '}
            <span className="text-xl font-medium text-gray-500">{intervalText}</span>
          </span>
          {coupon && (
            <SubscriptionBadge
              text={ft(messages.discountedPaywallBadge, {
                discount: getSavingsText(coupon, tier, selectedInterval)
              })}
              variant="green"
            />
          )}
        </p>
      </div>
      <div
        data-testid={`${tier}-tier-details`}
        className="p-[24px] flex flex-col justify-between flex-grow">
        <div className="text-xl font-normal space-y-4">
          {features.map((feature, index) => (
            <p key={index} className={index === 0 ? 'font-bold' : ''}>
              <FontAwesomeIcon
                icon={faCheck}
                width={15}
                height={15}
                style={{ marginRight: 5 }}
                color="#4B5563"
              />
              {feature}
            </p>
          ))}
        </div>
        <button
          type="button"
          onClick={() => onUpgrade(tier)}
          data-testid={`btn-upgrade-tier-${tier}`}
          disabled={loading || shouldDisallowSwitch(tier, user) || isCurrentPlan(tier)}
          className={`mt-10 no-underline block w-full rounded-[4px] transition ${
            isCurrentPlan(tier)
              ? 'bg-gray-100 text-gray-900 border border-solid border-gray-200'
              : 'bg-orange-is text-white border border-orange-is border-solid'
          } py-3 text-center text-xl font-semibold hover:opacity-70 ${
            loading || shouldDisallowSwitch(tier, user) ? 'cursor-not-allowed' : 'cursor-pointer'
          }`}>
          {loading && <LoadingSpinner loadingTextStyle="text-white" />}
          {getButtonText()}
        </button>
      </div>
    </div>
  );
};

// Helper to create upgrade handler that clears coupon and calls onUpgradeClick
const createUpgradeHandler = (
  onUpgradeClick: (tier: SubscriptionTier) => Promise<void>,
  onBuyClick: (tier: SubscriptionTier, cadence: Cadence) => Promise<void>,
  setCoupon: (coupon: PaywallCouponConfig | undefined) => void,
  selectedInterval: Cadence,
  coupon?: PaywallCouponConfig
) => {
  return async (tier: SubscriptionTier) => {
    if (coupon) {
      await onBuyClick(tier, selectedInterval);
      setCoupon(undefined);
    }
    await onUpgradeClick(tier);
  };
};

export const LegacyUpgrade = ({ store, intl }: Props) => {
  const { user } = store;
  const { formatMessage, ft } = intl;
  const [coupon, setCoupon] = useState<PaywallCouponConfig | undefined>(user?.paywallCouponInfo);
  const { pulsem_reviews } = useFlags(['pulsem_reviews']);

  const legacyUsageGroup = user.legacyUsageGroup;

  const showPlusLegacyUpgrade = legacyUsageGroup === 'legacy-invoice-limit-3';

  const paywallTiers = [
    ...(showPlusLegacyUpgrade
      ? [
          {
            tier: SubscriptionTier.PLUS,
            features: [
              ft(messages.invoicesPerMonthAmount, { invoices: 10 }),
              ft(messages.businessOwnerSignature),
              ft(messages.businessSummaryReports),
              ft(messages.featureDueDateReminders)
            ]
          }
        ]
      : []),
    {
      tier: legacyUsageGroup ? SubscriptionTier.PREMIUM : SubscriptionTier.PREMIUM_LEGACY,
      features: [
        ft(messages.proDocLimit),
        ...(pulsem_reviews.enabled ? [ft(messages.automatedReviews)] : []),
        ft(messages.clientSignatures),
        ft(messages.deposits),
        ft(messages.featurePremiumTemplates),
        ft(messages.versionHistory)
      ]
    }
  ];

  const { selectedInterval, toggleInterval, shouldDisallowSwitch } = useInterval(
    user.activeSubscriptionCadence
  );

  const {
    switching,
    loadingCheckout,
    onBuyClick,
    onUpgradeClick,
    OriginalPrice,
    FinalPrice,
    isCurrentPlan,
    getCouponPrices,
    showBookkeepingModal,
    closeBookkeepingModal,
    onBookkeepingAccept,
    onBookkeepingDecline,
    pendingPlanPriceCents,
    pendingTier,
    hasBaseSubscription,
    currencyCode
  } = useUpgrade({
    store,
    intl,
    selectedInterval,
    shouldDisallowSwitch
  });

  const loading = loadingCheckout || switching;
  const handleUpgrade = createUpgradeHandler(
    onUpgradeClick,
    onBuyClick,
    setCoupon,
    selectedInterval,
    coupon
  );

  useEffect(() => {
    const userTier = user.activeSubscriptionTier || undefined;
    const userCadence = user.activeSubscriptionCadence ?? selectedInterval;
    const feature = getURLQueryParam(URLQueryParamKeys.FEATURE) as FeatureName;
    const campaign = getURLQueryParam(URLQueryParamKeys.CAMPAIGN) as string;
    const ref = getURLQueryParam(URLQueryParamKeys.REF);

    user.events.trackAction('subscription-paywall', {
      ...store.getTieredPaywallEventData(userTier, userCadence),
      tier: userTier,
      from: feature ?? ref,
      campaign: campaign || undefined,
      path: ref,
      platform: Platform.WEB
    });

    /* Feature gating upgrade message */
    if (feature) {
      const { title, body } = getAlertMessageFromFeature(feature, intl);
      alertModel.setAlert('warning', title, body);
    }
  }, []);

  const onSupportClick = async () => {
    window.open('https://help.invoicesimple.com');
  };

  return (
    <div id="tailwind">
      <div
        className="relative mx-auto sm:py-24 px-4 sm:px-6 lg:px-8"
        style={{ maxWidth: '1000px' }}>
        <BackButton />
        <div className="sm:align-center sm:flex sm:flex-col">
          <div className="md:max-w-[650px] text-5xl font-bold text-gray-900 sm:text-center my-0 mx-auto">
            <img
              data-testid="is-logo"
              className="h-24 sm:h-28 w-auto rounded"
              src="images/is-logo.png"
              alt="Invoice Simple Logo"
            />
            <>
              <h2
                data-testid="upgrade-now-title"
                className="my-4 text-gray-900 font-bold"
                style={{ fontSize: 30, lineHeight: '36px' }}>
                {ft(messages.paywallTitle)}
              </h2>
              <p
                data-testid="upgrade-now-subtitle"
                className="text-gray-800 my-0 mx-auto"
                style={{ fontSize: 18, fontWeight: 400, lineHeight: '28px' }}>
                {ft(messages.paywallSubtitleUpgrade)}
              </p>
            </>
            <ValueProps paywallVariant={'premium-only'} />
          </div>
        </div>
        {coupon && (
          <div className="mt-[40px] md:mt-[60px] mx-auto">
            <h3
              className="text-center font-semibold mx-auto mb-[10px] sm:max-w-[500px] tracking-normal text-gray-900"
              style={{ fontSize: 20, lineHeight: '28px' }}>
              {getDiscountTranslation(coupon, ft, user)}
            </h3>

            <p className="text-center font-normal" style={{ fontSize: 14, lineHeight: '20px' }}>
              {ft(messages.discountedPaywallTableTopSubtitle, {
                duration: coupon.durationMonths
              })}
            </p>
          </div>
        )}
        <div className="mt-[20px]">
          <div className="bg-white pt-[10px] items-center flex flex-col sticky top-0">
            <PlanIntervalSelector
              selectedInterval={selectedInterval}
              onToggleInterval={toggleInterval}
              formatMessage={formatMessage}
              isHidden={user.hasAnnualSub}
            />

            <div className="flex flex-col md:flex-row justify-center gap-12 mt-[24px]">
              {paywallTiers.map((paywallTier) => (
                <PaywallCard
                  key={paywallTier.tier}
                  intl={intl}
                  tier={paywallTier.tier}
                  features={paywallTier.features}
                  selectedInterval={selectedInterval}
                  shouldDisallowSwitch={shouldDisallowSwitch}
                  user={user}
                  coupon={coupon}
                  loading={loading}
                  onUpgrade={handleUpgrade}
                  isCurrentPlan={isCurrentPlan}
                  OriginalPrice={OriginalPrice}
                  FinalPrice={FinalPrice}
                  getCouponPrices={getCouponPrices}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={onSupportClick}
              className="mt-12 !rounded-[6px] border-0 bg-gray-200 px-[21px] py-[13px] w-fit font-medium"
              style={{ fontSize: 14, lineHeight: '22px' }}>
              {ft(messages.discountedPaywallContactSupport)}
            </button>
          </div>
        </div>
      </div>
      <BookkeepingUpsellModal
        isOpen={showBookkeepingModal}
        onClose={closeBookkeepingModal}
        onUpgrade={onBookkeepingAccept}
        onDecline={onBookkeepingDecline}
        currentPlanPriceCents={pendingPlanPriceCents}
        cadence={selectedInterval}
        currencyCode={currencyCode}
        tierName={pendingTier ?? undefined}
        hasBaseSubscription={hasBaseSubscription}
      />
    </div>
  );
};
